import { ReceiveStateType } from "@/constants/receiveStateType"

export interface IntegrationColumn {
	key: string
	title: string
	dataType: string
	fieldExtensions?: any
	canEdit?: boolean
	setName?: boolean
}

export interface SetupColumn extends IntegrationColumn {

}

export interface Integration {
	id: string
	title: string
	description: string
	supportLink: string
	usesServices: boolean
	setupColumns: SetupColumn[]
	supportsNotifyOnError: boolean
	supportsTimeManagement: boolean
	csvSyncOptions: CSVSyncOptions
	supportsFiles?: boolean

	inbound: {
		mappableData: IntegrationColumn[]
		allowCustomEvents: boolean
		events: IntegrationEventMapping[] | null
		receiveStateType?: ReceiveStateType
		supportCustomFields?: boolean
		supportCustomEvents?: boolean
		supportFieldMappingsPerEvent?: boolean
		supportDynamicFieldSelection?: boolean
	} | null

	outbound: IntegrationOut | null
}

export interface CSVSyncOptions {
	supportCsvSync: boolean
	syncObjectType: SyncObjectType | null
}

export enum SyncObjectType {
	DataSource
}

export interface IntegrationEventMapping {
	key: string
	title: string
	description: string
	receiveState?: string
	templateId?: string

	inFieldMapping?: { [key: string]: string }
	inFieldConfiguration?: { [key: string]: InFieldConfiguration }
	extensions?: { [key: string]: any }
}

export interface IntegrationSettings {
	integrationId: string
	enabled: boolean
	configuration: { [key: string]: any }
	inFieldMapping: { [key: string]: string }
	eventMapping: { [key: string]: IntegrationEventMapping }
	filePathMappings?: FilePathMappingType[]
	outConfiguration: { [key: string]: OutConfiguration }
	notifyOnFailure?: string[]
	notifyByEmail: boolean
	fileUploadSettings?: { [key: string]: IntegrationUploadSettings }
	inFieldConfiguration?: { [key: string]: InFieldConfiguration }
}

export interface IntegrationWithSettings {
	integration: Integration
	settings: IntegrationSettings | null
}

export interface InFieldConfiguration {
	useRichText: boolean
}

export interface IntegrationSummary {
	integrationKey: string
	title: string
	description: string

	usesServices: boolean
	allowForceRun: boolean
}

export interface IntegrationStatus {
	lastChecked: Date | null
	nextCheck: Date | null
	startedProcessingAt?: Date | null
}

export interface IntegrationOutOption {
	integrationKey: string
	integrationTitle: string
	integrationOut: string
	integrationOutTitle: string
}

export enum ServiceStatus {
	Ok,
	Warning,
	Error,
	Offline
}

export interface IntegrationService {
	id: string
	environmentName: string
	status: ServiceStatus | null,
	statusText: string
}

export enum MessageType {
	Error,
	Warning,
	Information
}

export interface IntegrationLog {
	serviceId: string
	caseId: string
	occurred: Date
	messageType: MessageType
	message: string
}

export interface OutSettings {
	key: string
	title: string
	description: string
	setupColumns: IntegrationColumn[]
	supportsTrackingChanges: boolean
}

export interface IntegrationOut {
	supportRaise: boolean
	supportOutFieldMapping: boolean
	out: OutSettings[]
	mappableData:IntegrationColumn[]
}

export interface OutConfiguration {
	key: string
	outputType: string | null
	title: string
	enabled: boolean
	outFieldMapping: { [key: string]: string }
	configuration: { [key: string]: any }
	onDemandPermissions: string[] | null
}

export interface IntegrationUploadSettings {
	permissions: string[]
}

export interface IntegrationUploadOptions {
	integrationKey: string,
	integrationTitle: string,
    uploadOptions: {
        purpose: number,
        key: string,
        title: string,
        allowedFileExtensions: string[]
	}[]
}

export interface IntegrationUploadOptionsByIntegration {
	integrationKey: string,
	integrationTitle: string,
	uploadKey: string,
	uploadTitle: string,
	extensions: string[]
}

export enum IntegrationActionResultType {
	Fail = 0,
	PassedWithWarnings = 1,
	Success = 2
}

export interface IntegrationActionResult {
	result: IntegrationActionResultType,
	successMessages?: string[],
	warningMessages?: string[],
	errorMessages?: string[]
}

export interface FilePathMappingType {
	id: string, // Id of the CSV Sync Object (See SyncObjectType for possible object types)
	filePath: string,
	primaryColumn: string,
	fieldMapping: { [key: string]: string }
}
